/**
 * FooterSettingsPanel — the popover opened by clicking the Prompts footer bar.
 * Equation · Cadence pips · Engine tiles. G step 9 (§2.24 of the UI spec).
 */
import { useState, useEffect, useCallback } from 'react';
import type { ReactNode } from 'react';
import { Box, Button, CircularProgress, Tooltip } from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { AllowanceFigures, Cadence, SaveWarnings } from '@traaker/shared';
import { resolveAllowedEngines, selectableEnginesForAxis, launchesInPeriod, cadenceToDays, agencyPromptCreditsPerMonth } from '@traaker/shared';
import { EngineLogo, ENGINE_META, engineKey } from '../brand/EngineLogo';
import { QuotaWarningDialog } from './QuotaWarningDialog';
import { api } from '../../services/api';

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

export interface AgencyFooterFigures {
  promptCreditsPerMonth: number;
  nextLaunchAt?: string;
  availableCredits: number;
}

/** What a footer write sent (`PATCH audit-config`), handed to `onSaved`. */
export interface FooterChange {
  promptCadence?: Cadence;
  enginesEnabled?: string[];
}

export interface FooterSettingsPanelProps {
  figures: (AllowanceFigures & { cadence: Cadence; creditsPerMonth?: number }) | null;
  agencyFigures?: AgencyFooterFigures;
  tier: string;
  brandId: string;
  enginesEnabled?: string[];
  tenantEnginesEnabled?: string[];
  conversationCreditsPerMonth?: number;
  onSaved: (change: FooterChange) => void;
  onLaunch?: () => void;
}

/** The Prompts bar numbers on agency (credits per month, « Manuel »); self-serve keeps used / allowance. */
export function promptBarNums(
  figures: (AllowanceFigures & { cadence?: Cadence; creditsPerMonth?: number }) | null,
  isAgency: boolean,
  t: (k: string, o?: Record<string, unknown>) => string,
): string | undefined {
  if (!isAgency || !figures) return undefined;
  if (figures.cadence === 'off') return t('promptStudio.pop.manual');
  return t('promptStudio.pop.creditsResult', { n: figures.creditsPerMonth ?? 0 });
}

export interface EngineTile {
  id: string;
  active: boolean;
}

/** UI-SPEC §2.24 engine tiles: every engine the tier may select, the ones that run active. */
export function engineTiles(tier: string, enginesEnabled?: string[], tenantEnginesEnabled?: string[]): EngineTile[] {
  const active = new Set(resolveAllowedEngines(tier, enginesEnabled, tenantEnginesEnabled));
  return selectableEnginesForAxis('brand_narrative', tier).map((id) => ({ id, active: active.has(id) }));
}

/**
 * §2.24 [A] amber square: that cadence's prompt credits + the conversation credits per month
 * > the balance (the C17.3 sum). From `perLaunch`, which no cadence changes: a ratio over the
 * current cadence's launches is 0 / 0 on « Manuel », so no square was ever amber there.
 */
export function agencyPipAmber(pip: Cadence, perLaunch: number, conversationCreditsPerMonth: number, balance: number): boolean {
  if (pip === 'off') return false;
  return agencyPromptCreditsPerMonth({ perLaunch, cadence: pip }) + conversationCreditsPerMonth > balance;
}

/**
 * A tile's one-word label: the first word, except where it says nothing on its own
 * (« AI Overview » read « AI », « Google AI Mode » read « Google », owner 2026-11-02).
 */
export function tileLabel(key: string, label: string): string {
  if (key === 'ai_overview') return 'Overview';
  if (key === 'google_ai_mode') return 'AI Mode';
  return label.split(' ')[0];
}

/** An agency tile click: the active engines with that one toggled; null when none would be left (min 1). */
export function toggledEngines(tiles: EngineTile[], id: string): string[] | null {
  const next = tiles.filter((t) => (t.id === id ? !t.active : t.active)).map((t) => t.id);
  return next.length > 0 ? next : null;
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

type PipPreview = {
  loading: boolean;
  refused?: boolean;
  figuresAfter?: AllowanceFigures;
  warnings?: SaveWarnings[];
};

const SELF_SERVE_PIPS: Cadence[] = [1, 2, 3, 4, 5, 6, 7].map((d) => ({
  interval: d,
  unit: 'days' as const,
}));

const AGENCY_PIPS: Cadence[] = [
  { interval: 1, unit: 'days' },
  { interval: 2, unit: 'days' },
  { interval: 3, unit: 'days' },
  { interval: 5, unit: 'days' },
  { interval: 7, unit: 'days' },
  { interval: 14, unit: 'days' },
  { interval: 1, unit: 'months' },
  'off',
];

function cadenceKey(c: Cadence): string {
  return JSON.stringify(c);
}

/** Small bordered box used in the equation row. */
function EqBox({
  children,
  amber,
  primary,
}: {
  children: ReactNode;
  amber?: boolean;
  primary?: boolean;
}) {
  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: amber ? '#f59e0b' : primary ? 'rgba(124,58,237,.25)' : 'divider',
        borderRadius: '10px',
        px: 1.5,
        py: 1,
        minHeight: 42,
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        bgcolor: amber ? '#fffbeb' : primary ? 'rgba(124,58,237,.06)' : 'background.paper',
        fontSize: 15,
        fontWeight: 700,
        fontVariantNumeric: 'tabular-nums',
        color: amber ? '#b45309' : primary ? '#5b21b6' : 'text.primary',
      }}
    >
      {children}
    </Box>
  );
}

/** Operator symbol between equation boxes (×, =). */
function Op({ children }: { children: ReactNode }) {
  return (
    <Box sx={{ fontSize: 15, fontWeight: 600, color: 'text.disabled', mx: 0.125 }}>
      {children}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function FooterSettingsPanel({
  figures,
  agencyFigures,
  tier,
  brandId,
  enginesEnabled,
  tenantEnginesEnabled,
  conversationCreditsPerMonth,
  onSaved,
  onLaunch,
}: FooterSettingsPanelProps) {
  const { t } = useTranslation();
  const isAgency = !!agencyFigures;
  const pips = isAgency ? AGENCY_PIPS : SELF_SERVE_PIPS;

  const [pipPreviews, setPipPreviews] = useState<Record<string, PipPreview>>({});
  const [saving, setSaving] = useState(false);
  const [warningDialog, setWarningDialog] = useState<{
    open: boolean;
    warnings: SaveWarnings[];
    pendingAction: () => Promise<void>;
  } | null>(null);

  // Tiles = what the tier may select; the equation and the active tiles = what runs.
  const tiles = engineTiles(tier, enginesEnabled, tenantEnginesEnabled);
  const engines = tiles.filter((x) => x.active).map((x) => x.id);
  const engineCount = Math.max(1, engines.length);

  // Equation values
  const L = figures ? (figures.launchesInPeriod ?? launchesInPeriod(figures.cadence)) : 0;
  const promptCount = figures ? Math.round(figures.perLaunch / engineCount) : 0;

  // Amber result checks
  const selfServeOver = !isAgency && figures ? !figures.ok : false;
  const agencyOver =
    isAgency && agencyFigures
      ? agencyFigures.promptCreditsPerMonth + (conversationCreditsPerMonth ?? 0) >
        agencyFigures.availableCredits
      : false;
  const resultAmber = selfServeOver || agencyOver;

  const selectedKey = figures ? cadenceKey(figures.cadence) : '';

  // Self-serve: every square but the selected one is previewed in parallel, again after each
  // reload of the figures (a write, any in-popup change; UI-SPEC §2.24 [B]).
  useEffect(() => {
    if (!figures || isAgency) return;
    let live = true;
    const brandIdCapture = brandId;
    setPipPreviews({});
    SELF_SERVE_PIPS.forEach((pip) => {
      const key = cadenceKey(pip);
      if (key === selectedKey) return;
      setPipPreviews((prev) => ({ ...prev, [key]: { loading: true } }));
      api
        .post<{ refused?: boolean; figuresAfter?: AllowanceFigures }>(
          `/brands/${brandIdCapture}/queries/allowances/preview`,
          { cadence: pip },
        )
        .then((res) => {
          if (!live) return;
          setPipPreviews((prev) => ({
            ...prev,
            [key]: { loading: false, refused: res.refused, figuresAfter: res.figuresAfter },
          }));
        })
        .catch(() => {
          if (live) setPipPreviews((prev) => ({ ...prev, [key]: { loading: false } }));
        });
    });
    return () => { live = false; };
  }, [figures]); // eslint-disable-line react-hooks/exhaustive-deps

  // The write; the popup reloads its figures from `onSaved` (the popover stays open).
  const save = useCallback(
    async (change: FooterChange) => {
      setSaving(true);
      try {
        await api.patch(`/brands/${brandId}/audit-config`, change);
        onSaved(change);
      } finally {
        setSaving(false);
      }
    },
    [brandId, onSaved],
  );

  // Self-serve pip click: wait for preview if still loading, then save if not refused
  const handleSelfServePipClick = useCallback(
    async (pip: Cadence) => {
      const key = cadenceKey(pip);
      let preview = pipPreviews[key];

      if (preview?.refused) return;

      if (!preview || preview.loading) {
        setPipPreviews((prev) => ({ ...prev, [key]: { loading: true } }));
        try {
          const res = await api.post<{ refused?: boolean; figuresAfter?: AllowanceFigures }>(
            `/brands/${brandId}/queries/allowances/preview`,
            { cadence: pip },
          );
          preview = { loading: false, refused: res.refused, figuresAfter: res.figuresAfter };
          setPipPreviews((prev) => ({ ...prev, [key]: preview! }));
        } catch {
          setPipPreviews((prev) => ({ ...prev, [key]: { loading: false } }));
          return;
        }
        if (preview.refused) return;
      }

      await save({ promptCadence: pip });
    },
    [brandId, pipPreviews, save],
  );

  // Agency square or tile click (§2.24 [A]): the preview first; non-empty `warnings` → the
  // §2.25 dialog, empty → written at once.
  const handleAgencyChange = useCallback(
    async (change: FooterChange) => {
      let warnings: SaveWarnings[] = [];
      try {
        const res = await api.post<{ warnings?: SaveWarnings[] }>(
          `/brands/${brandId}/queries/allowances/preview`,
          change.promptCadence !== undefined ? { cadence: change.promptCadence } : { enginesEnabled: change.enginesEnabled },
        );
        warnings = res.warnings ?? [];
      } catch {
        // Preview failed — proceed without warning
      }

      const doSave = () => save(change);
      if (warnings.length > 0) {
        setWarningDialog({ open: true, warnings, pendingAction: doSave });
      } else {
        await doSave();
      }
    },
    [brandId, save],
  );

  // Agency engine tile toggle (min 1), from the engines that run now.
  const handleEngineToggle = (engineId: string) => {
    const next = toggledEngines(tiles, engineId);
    if (next) handleAgencyChange({ enginesEnabled: next });
  };

  // Cadence section header
  let cadSectionTitle = '';
  if (figures) {
    const c = figures.cadence;
    if (c === 'off') {
      cadSectionTitle = t('promptStudio.pop.manualTitle');
    } else if (c.unit === 'months') {
      cadSectionTitle = isAgency
        ? t('promptStudio.pop.cadTitleMonthly', { n: agencyFigures?.promptCreditsPerMonth ?? 0 })
        : t('promptStudio.pop.cadTitleMonthly', { n: L });
    } else {
      const d = cadenceToDays(c);
      cadSectionTitle = isAgency
        ? t('promptStudio.pop.cadTitleCredits', { d, n: agencyFigures?.promptCreditsPerMonth ?? 0 })
        : t('promptStudio.pop.cadTitle', { d, n: L });
    }
  }

  function getPipLabel(pip: Cadence): string {
    if (pip === 'off') return t('promptStudio.pop.manual');
    if (pip.unit === 'months') return `${pip.interval * 30}`;
    if (pip.unit === 'weeks') return `${pip.interval * 7}`;
    return `${pip.interval}`;
  }

  function getPipTooltip(pip: Cadence, pipKey: string): string {
    if (pip === 'off') return '';
    if (!isAgency) {
      const p = pipPreviews[pipKey];
      if (p?.refused) {
        const ff = p.figuresAfter;
        if (ff && ff.monthNeed > ff.allowance) return t('promptStudio.pop.overMonth');
        return t('promptStudio.pop.overBudget');
      }
    }
    return '';
  }

  function isPipAmber(pip: Cadence): boolean {
    if (!isAgency || !agencyFigures || !figures) return false;
    return agencyPipAmber(pip, figures.perLaunch, conversationCreditsPerMonth ?? 0, agencyFigures.availableCredits);
  }

  function isPipDisabled(_pip: Cadence, pipKey: string): boolean {
    if (saving) return true;
    if (!isAgency) return pipPreviews[pipKey]?.refused === true;
    return false;
  }

  return (
    <Box
      sx={{
        maxWidth: 'min(620px, calc(100% - 24px))',
        borderRadius: '12px',
        p: '14px 16px 15px',
        bgcolor: 'background.paper',
      }}
    >
      {/* Equation row */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap', mb: 1.5 }}>
        <EqBox>
          {promptCount} {promptCount === 1 ? 'prompt' : 'prompts'}
        </EqBox>
        <Op>×</Op>
        <EqBox>
          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
            {engines.map((e) => (
              <EngineLogo key={e} engine={e} size={22} />
            ))}
          </Box>
        </EqBox>
        <Op>×</Op>
        <EqBox>↻ {L}</EqBox>
        <Op>=</Op>
        <EqBox amber={resultAmber} primary={!resultAmber}>
          {isAgency
            ? `${agencyFigures?.promptCreditsPerMonth ?? 0} crédits / mois`
            : figures
              ? `${figures.sustainableNeed} / ${figures.allowance} par mois`
              : '—'}
        </EqBox>
      </Box>

      {/* Config-over warning line (self-serve, over quota) */}
      {selfServeOver && (
        <Box sx={{ fontSize: 12, color: '#b45309', mb: 1.5 }}>
          {t('promptStudio.pop.configOver')}
        </Box>
      )}

      {/* Cadence section */}
      <Box sx={{ mb: 1.5 }}>
        {cadSectionTitle && (
          <Box
            sx={{
              fontSize: 11,
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '.04em',
              color: 'text.secondary',
              mb: 0.75,
            }}
          >
            {cadSectionTitle}
          </Box>
        )}
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
          {pips.map((pip) => {
            const key = cadenceKey(pip);
            const isSelected = key === selectedKey;
            const disabled = isPipDisabled(pip, key);
            const amber = isPipAmber(pip);
            const label = getPipLabel(pip);
            const tooltip = getPipTooltip(pip, key);
            const isLoadingPreview = !isAgency && pipPreviews[key]?.loading === true;

            return (
              <Tooltip key={key} title={tooltip} arrow disableHoverListener={!tooltip}>
                <Box
                  component="button"
                  onClick={() => {
                    if (disabled || isSelected || saving) return;
                    if (isAgency) handleAgencyChange({ promptCadence: pip });
                    else handleSelfServePipClick(pip);
                  }}
                  sx={{
                    width: 34,
                    height: 32,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '1px solid',
                    borderColor: isSelected ? '#7c3aed' : amber ? '#f59e0b' : 'divider',
                    borderRadius: 1,
                    bgcolor: isSelected
                      ? '#7c3aed'
                      : amber
                        ? '#fffbeb'
                        : 'background.paper',
                    color: isSelected
                      ? '#fff'
                      : amber
                        ? '#b45309'
                        : disabled
                          ? 'text.disabled'
                          : 'text.primary',
                    fontSize: label.length > 3 ? 9 : 12,
                    fontWeight: 600,
                    cursor: disabled || isSelected ? 'default' : 'pointer',
                    opacity: disabled ? 0.45 : 1,
                    p: 0,
                    '&:hover':
                      !disabled && !isSelected && !saving
                        ? {
                            bgcolor: 'rgba(124,58,237,.06)',
                            borderColor: 'rgba(124,58,237,.3)',
                          }
                        : {},
                  }}
                >
                  {isLoadingPreview ? <CircularProgress size={10} color="inherit" /> : label}
                </Box>
              </Tooltip>
            );
          })}
        </Box>

        {/* Agency-only manual launch button */}
        {isAgency && onLaunch && (
          <Button
            variant="outlined"
            size="small"
            onClick={onLaunch}
            sx={{ mt: 1, fontWeight: 600, fontSize: 12 }}
          >
            {/* i18n: promptStudio.launch.title */}
            Lancer
          </Button>
        )}
      </Box>

      {/* Engine section */}
      <Box>
        <Box
          sx={{
            fontSize: 11,
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '.04em',
            color: 'text.secondary',
            mb: 0.75,
          }}
        >
          {t('promptStudio.pop.engines')}
        </Box>
        <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
          {tiles.map(({ id: engineId, active: isActive }) => {
            const k = engineKey(engineId);
            const meta = ENGINE_META[k];
            const isClickable = isAgency;

            return (
              <Box
                key={engineId}
                component={isClickable ? 'button' : 'div'}
                onClick={isClickable && !saving ? () => handleEngineToggle(engineId) : undefined}
                sx={{
                  width: 46,
                  height: 42,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 0.3,
                  border: '1px solid',
                  borderColor: isActive ? `${meta.color}40` : 'divider',
                  borderRadius: 1.5,
                  bgcolor: isActive ? meta.bg : 'transparent',
                  opacity: isActive ? 1 : 0.45,
                  cursor: isClickable ? 'pointer' : 'default',
                  p: 0,
                  flexShrink: 0,
                  '&:hover': isClickable ? { borderColor: `${meta.color}80` } : {},
                }}
              >
                <EngineLogo engine={engineId} size={22} />
                <Box
                  sx={{
                    fontSize: 8.5,
                    fontWeight: 600,
                    color: 'text.secondary',
                    lineHeight: 1,
                    textAlign: 'center',
                    px: 0.25,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    maxWidth: 44,
                  }}
                >
                  {tileLabel(k, meta.label)}
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>

      {/* Warning dialog (agency over-balance confirmation) */}
      {warningDialog && (
        <QuotaWarningDialog
          open={warningDialog.open}
          warnings={warningDialog.warnings}
          onConfirm={async () => {
            const action = warningDialog.pendingAction;
            setWarningDialog(null);
            await action();
          }}
          onCancel={() => setWarningDialog(null)}
        />
      )}
    </Box>
  );
}
